import { useAuth0 } from '@auth0/auth0-react';
import * as proximaSdk from '@innovationdepartment/proxima-sdk-axios';
import { useState } from 'react';
import { useBrandStore } from 'stores';
import { isUnrecoverableAuthError } from 'utils';

type ExtractApiMembers<Module> = {
  [Name in keyof Module]: Name extends `${string}Api` ? Name : never;
}[keyof Module];

type SDKApiName = ExtractApiMembers<typeof proximaSdk>;
type EndpointInstance<T extends SDKApiName> = InstanceType<(typeof proximaSdk)[T]> & {
  loading: boolean;
};

const { Configuration } = proximaSdk;

const PLATFORM_APIS = ['BrandsApi', 'UsersApi', 'CreativeEngineApi', 'BrandProfileApi'];
const INTEGRATION_APIS = [
  'IntegrationsApi',
  'FbIntegrationApi',
  'KlaviyoIntegrationApi',
  'TiktokIntegrationApi',
  'ShopifyIntegrationApi'
];
const WORKFLOWS_APIS = ['ExecutionsApi', 'SchedulersApi'];

type ProximaSDKOptions = {
  /** Base URL for the api, in place of the one picked by api name. */
  basePath?: string;
  /**
   * Who supplies the `Authorization` header.
   *
   * - `'auth0'` (default): each call gets a brand-scoped Auth0 token and logs out on an unrecoverable session.
   * - `'caller'`: the hook never touches Auth0, and the Configuration has no `accessToken`, so the caller passes the header in each method's request options.
   */
  authorization?: 'auth0' | 'caller';
  /**
   * Whether calls update `loading` (default true).
   *
   * Pass false when nothing reads `loading`, so calls cause no re-renders.
   */
  trackLoading?: boolean;
};

const useProximaSDK = <T extends SDKApiName>(
  apiName: T,
  options: ProximaSDKOptions = {}
): EndpointInstance<T> => {
  const { authorization = 'auth0', trackLoading = true } = options;
  const { getAccessTokenSilently, logout } = useAuth0();
  const { brand } = useBrandStore();

  const [loading, setLoading] = useState<Record<string, boolean>>({});

  const proxy = new Proxy(
    {},
    {
      get(_, prop) {
        if (prop === 'loading') {
          // if any element of this sdk instance is loading, return true
          return Object.values(loading).some(value => value);
        }

        const getAuth0Token = async (): Promise<string> => {
          const audience = import.meta.env.VITE_AUTH0_AUDIENCE;
          // without an audience Auth0 mints a token our API rejects; the env var is
          // baked in at build time, so no runtime retry or re-login can recover
          if (!audience) throw new Error('VITE_AUTH0_AUDIENCE is not configured');

          try {
            return await getAccessTokenSilently({
              authorizationParams: {
                brandId: brand?.brandId,
                audience
              }
            });
          } catch (error) {
            // the session can't be renewed silently; clear local auth state so
            // <App /> sends the user back through an interactive login
            if (isUnrecoverableAuthError(error)) await logout({ openUrl: false });
            throw error;
          }
        };

        return async (...props: unknown[]) => {
          // a caller-authorized call sends its own header, so it needs no Auth0 token
          const accessToken = authorization === 'auth0' ? await getAuth0Token() : undefined;

          // create proxima sdk configuration
          let basePath = import.meta.env.VITE_PROXIMA_API_URL;
          if (PLATFORM_APIS.includes(apiName)) {
            // If the api is a platform api, use the platform api url
            // This is temporary until we move all APIs to the platform api
            basePath = import.meta.env.VITE_PLATFORM_API_URL;
          }
          if (INTEGRATION_APIS.includes(apiName)) {
            // If the api is an integration api, use the integration api url
            basePath = import.meta.env.VITE_PROXIMA_INTEGRATIONS_API_URL;
          }
          if (WORKFLOWS_APIS.includes(apiName)) {
            // If the api is a workflows api, use the workflows api url
            basePath = import.meta.env.VITE_WORKFLOWS_API_URL;
          }

          if (options.basePath) basePath = options.basePath;

          const config = new Configuration({
            basePath,
            ...(accessToken ? { accessToken } : {}),
            baseOptions: { headers: { Accept: 'application/json' } }
          });

          // create proxima sdk api instance
          // biome-ignore lint/performance/noDynamicNamespaceImportAccess: api class is selected at runtime by the proxy
          const apiInstance = new proximaSdk[apiName](config);

          // call proxima sdk api method
          const method = apiInstance[prop as keyof typeof apiInstance];

          const markLoading = (value: boolean) => {
            if (trackLoading) setLoading(prev => ({ ...prev, [prop]: value }));
          };

          try {
            markLoading(true);

            // return proxima sdk api method result
            // use unknown to avoid typescript error
            // use .call to allow passing in the api instance as the this context
            const result = await (method as unknown as (...args: unknown[]) => unknown).call(
              apiInstance,
              ...props
            );
            markLoading(false);
            return result;
          } catch (error) {
            markLoading(false);
            throw error;
          }
        };
      }
    }
  );

  // return proxima sdk api instance
  // make typescript happy by casting to the correct type called by the api name
  return proxy as EndpointInstance<T>;
};

export default useProximaSDK;
